import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';
import Looks5Icon from '@mui/icons-material/Looks5';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// ⚠️ Replace with real WhatsApp number (international format without + or spaces)
const WHATSAPP_NUMBER = '50378405400';
const WHATSAPP_MESSAGE = 'Hello! I am interested in starting my admission process at American College. Could you provide more information?';

const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// --- Page Data ---
const steps = [
  {
    icon: <LooksOneIcon />,
    title: 'Request Information',
    description: 'Contact us via WhatsApp or visit us. We will explain everything about our programs, costs, and available scholarships.',
    color: '#2B2B6B',
  },
  {
    icon: <LooksTwoIcon />,
    title: 'Submit Documentation',
    description: 'Gather the required documents and submit them at our offices or digitally.',
    color: '#E31E24',
  },
  {
    icon: <Looks3Icon />,
    title: 'Admission Exam',
    description: 'Take our general knowledge and English proficiency test. We prepare you so you can feel confident.',
    color: '#00CED1',
  },
  {
    icon: <Looks4Icon />,
    title: 'Interview',
    description: 'A brief chat with our academic team to get to know you better and guide you in your career path.',
    color: '#2B2B6B',
  },
  {
    icon: <Looks5Icon />,
    title: 'Welcome to American College!',
    description: 'Receive your acceptance letter, complete your enrollment, and begin your new academic journey.',
    color: '#E31E24',
  },
];

const requirements = [
  'High School Diploma or 11th-grade certificate (minimum)',
  'Identification document (ID or Passport)',
  '2 recent passport-sized photographs',
  'Birth certificate',
  'High school transcript/grades',
  'Recommendation letter (optional but valued)',
];

const importantDates = [
  { label: 'Open Enrollment', date: 'Year-round', chip: 'Open', color: '#2B2B6B' },
  { label: 'Term I Start', date: 'January 2026', chip: 'Upcoming', color: '#E31E24' },
  { label: 'Term II Start', date: 'July 2026', chip: 'Future', color: '#00CED1' },
  { label: 'Academic Scholarship Deadline', date: '15th of every month', chip: 'Recurring', color: '#FF6B35' },
];

const faqs = [
  {
    q: 'Can I study if I work?',
    a: 'Yes. We offer morning, afternoon, and Saturday schedules to adapt to your availability.',
  },
  {
    q: 'Are classes taught in English?',
    a: 'A large part of the program is bilingual. You do not need to master English to enter; we train you starting from your current level.',
  },
  {
    q: 'Can I transfer to a foreign university?',
    a: 'Yes. This is one of our key differentiators: we have agreements with over 15 international universities for you to continue your career.',
  },
  {
    q: 'What if I do not qualify for a scholarship?',
    a: 'We have flexible payment plans and financing options. Tell us about your situation, and we will find a solution.',
  },
];

// --- Component ---
const Admissions = () => {
  return (
    <>
      <PageHeader
        title="Admission Process"
        subtitle="Your new academic stage starts here. We accompany you every step of the way."
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Admissions', href: '/admissions' },
        ]}
      />

      {/* HERO CTA */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2B2B6B 0%, #1a1a4a 100%)',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.6rem' },
            }}
          >
            Have questions? Message us now
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, fontSize: '1.1rem' }}>
            Our admissions team will respond in minutes
          </Typography>
          <Button
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<WhatsAppIcon sx={{ fontSize: 28 }} />}
            sx={{
              backgroundColor: '#25D366',
              color: 'white',
              fontSize: '1.1rem',
              fontWeight: 700,
              px: 5,
              py: 2,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 8px 32px rgba(37,211,102,0.4)',
              '&:hover': {
                backgroundColor: '#1ebe5d',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(37,211,102,0.5)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Chat with Admissions on WhatsApp
          </Button>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mt: 2, fontSize: '0.85rem' }}>
            Monday to Friday 8:00 AM – 5:00 PM · Saturdays 8:00 AM – 12:00 PM
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>

        {/* PROCESS STEPS */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Step by Step" sx={{ backgroundColor: '#E31E2415', color: '#E31E24', fontWeight: 600, mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#2B2B6B', fontWeight: 700 }}>
              How the process works
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            {/* Connector Line (desktop only) */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 40,
                left: '10%',
                right: '10%',
                height: '2px',
                background: 'linear-gradient(90deg, #2B2B6B, #E31E24, #00CED1, #2B2B6B, #E31E24)',
                zIndex: 0,
              }}
            />

            <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              {steps.map((step, index) => (
                <Grid item xs={12} sm={6} md key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        backgroundColor: step.color,
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        boxShadow: `0 8px 24px ${step.color}40`,
                        border: '4px solid white',
                        '& svg': { fontSize: 36 },
                      }}
                    >
                      {step.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ color: '#2B2B6B', fontWeight: 700, mb: 1, fontSize: '1rem' }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, px: 1 }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Grid container spacing={6}>
          {/* REQUIREMENTS */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#2B2B6B', mr: 2 }}>
                <DescriptionIcon />
              </Avatar>
              <Typography variant="h5" sx={{ color: '#2B2B6B', fontWeight: 700 }}>
                Admission Requirements
              </Typography>
            </Box>
            <Paper elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 3 }}>
              <List sx={{ p: 0 }}>
                {requirements.map((req, i) => (
                  <ListItem key={i} sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#2B2B6B', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={req}
                      primaryTypographyProps={{ fontSize: '0.95rem', color: '#444' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                Have questions about a specific document? Write to us and we will guide you.
              </Typography>
            </Paper>
          </Grid>

          {/* IMPORTANT DATES */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ backgroundColor: '#E31E24', mr: 2 }}>
                <CalendarMonthIcon />
              </Avatar>
              <Typography variant="h5" sx={{ color: '#2B2B6B', fontWeight: 700 }}>
                Important Dates
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {importantDates.map((item, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: 2,
                    borderLeft: `4px solid ${item.color}`,
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: `0 4px 16px ${item.color}25` },
                  }}
                >
                  <CardContent sx={{ py: '12px !important', px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#2B2B6B' }}>
                        {item.date}
                      </Typography>
                    </Box>
                    <Chip
                      label={item.chip}
                      size="small"
                      sx={{ backgroundColor: `${item.color}15`, color: item.color, fontWeight: 600 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* FAQS */}
        <Box sx={{ mt: 10 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar sx={{ backgroundColor: '#00CED1', mr: 2 }}>
              <HelpOutlineIcon />
            </Avatar>
            <Typography variant="h5" sx={{ color: '#2B2B6B', fontWeight: 700 }}>
              Frequently Asked Questions
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {faqs.map((faq, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid #E0E0E0',
                    borderRadius: 2,
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#00CED1', boxShadow: '0 4px 16px rgba(0,206,209,0.15)' },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#2B2B6B', mb: 1 }}>
                    {faq.q}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                    {faq.a}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FINAL CTA */}
        <Paper
          elevation={0}
          sx={{
            mt: 10,
            p: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #E31E24 0%, #C41A1F 100%)',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <SchoolIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.8)', mb: 2 }} />
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, mb: 1.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Your future begins with a message
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontSize: '1.05rem' }}>
            Don't wait any longer. Write to us and an advisor will guide you with no obligation.
          </Typography>
          <Button
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<WhatsAppIcon sx={{ fontSize: 26 }} />}
            sx={{
              backgroundColor: 'white',
              color: '#25D366',
              fontSize: '1.05rem',
              fontWeight: 700,
              px: 5,
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#f0faf4',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Message on WhatsApp
          </Button>
        </Paper>

      </Container>
    </>
  );
};

export default Admissions;